import { useEffect, useRef } from 'react';
import { ActionModal, Button, colors, DropInput } from 'common-components';
import { XLSX_FILE_TYPE } from '@utils';
import { RcFile } from 'antd/lib/upload';
import { UploadChangeParam } from 'antd/es/upload/interface';
import { LoadingOutlined } from '@ant-design/icons';
import { useAppTranslation } from '@hooks';

import { store } from '@/store';

type ImportFilesModalPropsType = {
  isLoading: boolean
  useDisclosure: { isOpen: boolean; close: () => void }
  onFileUpload: (file: File) => void
  downloadSampleFile: () => Promise<void>
};
const ImportFilesModal = ({
  isLoading,
  useDisclosure,
  onFileUpload,
  downloadSampleFile,
}: ImportFilesModalPropsType) => {
  const addErrorNotificationAction = store.use.addErrorNotificationAction();
  const { translate } = useAppTranslation();

  const uploadFile = useRef<RcFile | ''>('');

  useEffect(() => {
    uploadFile.current = '';
  }, []);

  const onUpload = async (ev: UploadChangeParam) => {
    try {
      const fileObj = ev.fileList[0]?.originFileObj;
      uploadFile.current = fileObj;
      if (fileObj?.size) {
        if (fileObj.type.split('/')[1] !== XLSX_FILE_TYPE) {
          addErrorNotificationAction({
            description: translate('common.importFiles.onlyXlsxAccepted'),
          });
          return;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      addErrorNotificationAction();
    }
  };

  const onUploadConfirm = () => {
    if (uploadFile.current === '') return;
    onFileUpload(uploadFile.current);
  };

  return (
    <ActionModal
      open={useDisclosure.isOpen}
      icon="UploadCloud02"
      iconColor={colors.gray600}
      iconBgColor={colors.gray100}
      iconBorderColor={colors.gray50}
      title={translate('common.importFiles.title')}
      onConfirm={onUploadConfirm}
      onCancel={() => useDisclosure.close()}
      actionButtons={[
        {
          text: translate('common.buttons.cancel'),
          onClick: () => useDisclosure.close(),
        },
        {
          text: isLoading ? <LoadingOutlined /> : translate('common.importFiles.importButton'),
          type: 'primary',
          disabled: isLoading,
          onClick: onUploadConfirm,
        },
      ]}
    >
      <DropInput beforeUpload={() => false} maxCount={1} onUpload={onUpload} />
      <Button type="link" size="sm" style={{ padding: 0, marginTop: 12 }} onClick={downloadSampleFile}>
        {translate('common.importFiles.downloadSampleFile')}
      </Button>
    </ActionModal>
  );
};

export default ImportFilesModal;
